import {Tabs, CustomItem} from '@jetbrains/ring-ui/components/tabs/tabs'
import classNames from 'classnames'
import {memo, useMemo, useCallback, Suspense} from 'react'
import {EntryPointContainer} from 'react-relay'
import {useLocation, useNavigate} from 'react-router-dom'

import BuildsFetcher from '../../../containers/BuildsFetcher'
import {EntityProvider} from '../../../contexts/entity'
import {useQueryParam} from '../../../hooks/routes'
import {useTabs} from '../../../hooks/useTabs'
import {getHrefWithQueryParams} from '../../../routes'
import {calcNextTab, getTabUrl} from '../../../selectors'
import {
  ClassicUIBuildTabNamesEnum,
  BuildPageTabNamesEnum,
  BuildPageTabTitlesEnum,
  stringifyId,
} from '../../../types'
import type {Tab, TabId} from '../../../types'
import {emptyArray} from '../../../utils/empty'
import {parseTabTitle} from '../../../utils/tabs'
import ClassicUITabAdapter from '../../common/ClassicUITabAdapter/ClassicUITabAdapter'
import Loader from '../../common/Loader/Loader'
import {useMode} from '../../common/ModeSwitch/ModeSwitch'
import OverviewTab, {
  createClassicUIOverviewTab,
  renderTitle,
} from '../../common/OverviewTab/OverviewTab'
import tabStyles from '../../common/OverviewTab/OverviewTab.css'
import MammothHint from '../../common/OverviewTab/OverviewTab.mammoth'
import TabPlugin from '../../common/Plugin/TabPlugin/TabPlugin'
import TabsLoader from '../../common/TabsLoader/TabsLoader'

import BuildArtifactsTab from './BuildArtifactsTab/BuildArtifactsTab'
import BuildChangesTab from './BuildChangesTab/BuildChangesTab'
import BuildLogTab from './BuildLogTab/BuildLogTab.container'
import type {Props} from './BuildPage.types'
import BuildPageHeader from './BuildPageHeader/BuildPageHeader.container'
import {buildParametersTabParam} from './BuildParametersTab/BuildParameters.constants'
import {BuildParametersKindTab} from './BuildParametersTab/BuildParameters.types'
import {BuildParametersTab} from './BuildParametersTab/BuildParametersTab'
import {isNewBuildParametersTab} from './BuildParametersTab/utils/isNewBuildParametersTab'
import DependenciesTab from './DependenciesTab/DependenciesTab.lazy'
import {DependenciesTypes, SnapshotDependenciesModes} from './DependenciesTab/DependenciesTab.modes'

import styles from './BuildPage.css'

import {useAppSelector} from 'src/hooks/react-redux'

const BuildPage = (props: Props) => {
  const {
    buildTypeId,
    buildId,
    buildLocator,
    buildLoaded,
    buildTypeLoaded,
    projectLoaded,
    queued,
    buildState,
    overviewTabEntryPoint,
    dependenciesTabEntryPoint,
    testsTabEntryPoint,
  } = props

  const mode = useMode()
  const type = useQueryParam('type', DependenciesTypes.SNAPSHOT)
  const buildParameterTab = useQueryParam(buildParametersTabParam)

  const location = useLocation()
  const navigate = useNavigate()
  const currentTabId = useQueryParam('buildTab')
  const currentTab = useAppSelector(state => calcNextTab(state, {tab: currentTabId, buildId}))
  const {
    tabs: {sakuraTabs, classicTabs},
    allTabsReady,
  } = useTabs(props)

  const onSelect = useCallback(
    (buildTab: TabId) =>
      navigate(getHrefWithQueryParams(location, null, ({branch}) => ({buildTab, branch}))),
    [location, navigate],
  )

  const overviewTab = useMemo(
    () =>
      overviewTabEntryPoint && (
        <EntryPointContainer
          entryPointReference={overviewTabEntryPoint}
          props={{buildId, buildLocator}}
        />
      ),
    [overviewTabEntryPoint, buildId, buildLocator],
  )
  const testsTab = useMemo(
    () =>
      testsTabEntryPoint && (
        <EntryPointContainer
          entryPointReference={testsTabEntryPoint}
          props={{buildId, buildLocator}}
        />
      ),
    [testsTabEntryPoint, buildId, buildLocator],
  )

  const getImplementedInSakuraUITabs = useCallback(
    (id: TabId) => {
      switch (id) {
        case BuildPageTabNamesEnum.OVERVIEW:
          return overviewTab
        case BuildPageTabNamesEnum.CHANGES:
          return <BuildChangesTab />
        case BuildPageTabNamesEnum.DEPENDENCIES:
          return <DependenciesTab buildId={buildId} entryPoint={dependenciesTabEntryPoint} />
        case BuildPageTabNamesEnum.LOG:
          return <BuildLogTab buildId={buildId} />
        case BuildPageTabNamesEnum.TESTS:
          return testsTab
        case BuildPageTabNamesEnum.ARTIFACTS:
          return <BuildArtifactsTab buildId={buildId} />
        case BuildPageTabNamesEnum.PARAMETERS:
          return isNewBuildParametersTab() ? <BuildParametersTab buildId={buildId} /> : null
        default:
          return null
      }
    },
    [overviewTab, buildId, dependenciesTabEntryPoint, testsTab],
  )
  const renderTab = useCallback(
    (tab?: Tab) => {
      let tabId = currentTab
      let titleText =
        BuildPageTabTitlesEnum[currentTab] ??
        stringifyId(currentTab).charAt(0).toUpperCase() + stringifyId(currentTab).slice(1)
      let title = renderTitle(titleText)

      if (tab != null) {
        const {name, counter} = parseTabTitle(tab.title)
        tabId = tab.id
        titleText = name
        title = renderTitle(name, counter)
      }

      if (tab?.onlyClassicUI === true) {
        return createClassicUIOverviewTab({id: tabId, title: titleText, href: tab.href})
      } else {
        const tabContent = getImplementedInSakuraUITabs(tabId)
        const Wrapper = tab?.sakura === true ? TabPlugin : ClassicUITabAdapter

        return (
          <OverviewTab
            key={stringifyId(tabId)}
            id={tabId}
            title={title}
            titleProps={{'aria-label': titleText}}
            className={classNames({
              [styles.fullHeight]:
                tabId === BuildPageTabNamesEnum.PARAMETERS &&
                buildParameterTab === BuildParametersKindTab.PARAMETERS,
            })}
            isClassic={
              tabContent == null ||
              (tabId === BuildPageTabNamesEnum.PARAMETERS &&
                buildParameterTab === BuildParametersKindTab.STATISTICS)
            }
            href={getTabUrl({buildId, buildTypeId}, tabId)}
            unlimitedWidth={
              (tabId === ClassicUIBuildTabNamesEnum.dependencies &&
                type === DependenciesTypes.SNAPSHOT &&
                mode !== SnapshotDependenciesModes.LIST) ||
              tabId === BuildPageTabNamesEnum.LOG
            }
          >
            <Suspense fallback={<Loader data-suspense-fallback />}>
              {tabContent || (
                <Wrapper
                  tabId={tabId}
                  title={titleText}
                  buildId={buildId}
                  queued={queued}
                  js={tab?.js}
                  css={tab?.css}
                  url={tab?.url}
                />
              )}
            </Suspense>
          </OverviewTab>
        )
      }
    },
    [
      currentTab,
      getImplementedInSakuraUITabs,
      buildId,
      buildTypeId,
      type,
      mode,
      queued,
      buildParameterTab,
    ],
  )
  const classicUITabs = useMemo(
    () => (classicTabs.length > 0 ? classicTabs.map(renderTab) : emptyArray),
    [renderTab, classicTabs],
  )
  const sakuraUITabs = useMemo(
    () => (sakuraTabs.length ? sakuraTabs.map(renderTab) : [renderTab()]),
    [sakuraTabs, renderTab],
  )
  return useMemo(
    () => (
      <EntityProvider buildId={buildId}>
        <BuildsFetcher
          locator={`id:${stringifyId(buildId)}`}
          buildTypeId={buildTypeId}
          withQueued
          withRunning
        >
          {buildLoaded && buildTypeLoaded && projectLoaded ? (
            <>
              <BuildPageHeader buildId={buildId} />
              <Tabs
                className={tabStyles.tabs}
                key={`${stringifyId(buildId)}${buildState === 'queued' ? '_queued' : ''}`}
                selected={currentTab}
                onSelect={onSelect}
                data-test="overview-tabs-panel"
                initialVisibleItems={5}
                autoCollapse={process.env.NODE_ENV !== 'test'}
                morePopupBeforeEnd={
                  (classicTabs.some(tab => tab.onlyClassicUI) && <MammothHint />) || null
                }
                morePopupItemClassName={styles.morePopupItemClassName}
              >
                {sakuraUITabs}
                {classicUITabs}
                {!allTabsReady ? (
                  <CustomItem key="tabs_loader">
                    <TabsLoader />
                  </CustomItem>
                ) : null}
              </Tabs>
            </>
          ) : (
            <Loader className={styles.loader} />
          )}
        </BuildsFetcher>
      </EntityProvider>
    ),
    [
      buildId,
      buildTypeId,
      buildLoaded,
      buildTypeLoaded,
      projectLoaded,
      buildState,
      currentTab,
      onSelect,
      classicTabs,
      sakuraUITabs,
      classicUITabs,
      allTabsReady,
    ],
  )
}

export default memo(BuildPage)
